
import { getAiResponse } from "@/utils/ai";
import { debugLog, DebugLevel } from "@/utils/debugUtils";
import { saveAiWordForNextRound } from "./databaseOperations";
import { handleRoundPreparationError } from "./uiHandlers";
import { captureRoundPreparationState, updateRoundsWithProximity } from "./stateManagement";

/**
 * Generates the AI's word for the next round
 */
export const generateNextAiWord = async (
  gameId: string,
  currentRoundNumber: number,
  userWord: string,
  aiWord: string,
  allUsedWords: string[],
  setBotIsThinking: (isThinking: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setRounds: (rounds: any) => void,
  toast: any
): Promise<{ success: boolean; aiWord?: string; explanation?: string }> => {
  setBotIsThinking(true);
  debugLog(DebugLevel.INFO, "roundPreparation", "Set botIsThinking to true");
  
  // Capture state before AI generation
  captureRoundPreparationState(
    "beforeAI", 
    gameId, 
    currentRoundNumber + 1,
    null, // userWord not defined yet for next round
    null, // aiWord not defined yet for next round
    false, // userHasSubmitted
    false, // botHasChosenWord
    true   // botIsThinking
  );
  
  try {
    debugLog(DebugLevel.INFO, "roundPreparation", "Starting AI word generation for next round");
    
    // Call the AI service to generate the next word
    const { nextAiWord, explanation } = await getAiResponse(userWord, aiWord, allUsedWords);
    debugLog(DebugLevel.INFO, "roundPreparation", `Generated AI word '${nextAiWord}' for next round`);
    
    // Save the AI's word to the database
    const saveSuccess = await saveAiWordForNextRound(
      gameId,
      currentRoundNumber + 1,
      nextAiWord,
      explanation
    );
    
    if (!saveSuccess) {
      handleRoundPreparationError('saving-word', toast);
    }
    
    // Update UI to reflect that the bot has chosen its word
    setBotHasChosenWord(true);
    debugLog(DebugLevel.INFO, "roundPreparation", "Set botHasChosenWord to true");
    
    // Capture state after AI generation
    captureRoundPreparationState(
      "complete", 
      gameId, 
      currentRoundNumber + 1,
      null, // userWord not defined yet for next round
      nextAiWord,
      false, // userHasSubmitted
      true,  // botHasChosenWord
      false  // botIsThinking (will be set to false by caller)
    );
    
    // Update the rounds state without adding a new round
    updateRoundsWithProximity(setRounds, currentRoundNumber);
    
    return { success: true, aiWord: nextAiWord, explanation };
  } catch (error) {
    debugLog(DebugLevel.ERROR, "roundPreparation", "Error getting AI response", error);
    handleRoundPreparationError('ai-word', toast);
    return { success: false };
  }
};
